import { api, appointmentDraftSelectors, bookingsApi, loadClientDetails } from '~/api';
import { getServiceConfig, questionsActions } from '~/components';
import { config as envConfig } from 'ENV_CONFIG';

import { appointmentApi } from './appointment.api';
import { buildCreateAppointmentRequest } from './appointment.api.helpers';
import { loadPartners } from '~/routes/partners/partners.actions';
import { appointmentDraftActions } from './appointment-draft.actions';
import { setConfig } from '~/ocab-services/services.actions';
import { restoreAnswers } from '~/components/questions/helpers';
import { appointmentRequestHelpers } from './appointment.request.helpers';

const actionPrefix = (actionType) => `Appointment/${actionType}`;

export const SET_APPOINTMENT = actionPrefix`SET_APPOINTMENT`;
export const setAppointment = (appointment) => ({
  type: SET_APPOINTMENT,
  payload: appointment,
});

export const getAppointment =
  (storeId, bypassErrorCodes = []) =>
  (dispatch, getState) => {
    const state = getState();

    const mainAppointmentTypeId = appointmentDraftSelectors.selectAppointmentTypeId(state);
    const relatedAppointmentTypesIds =
      appointmentDraftSelectors.selectRelatedAppointmentTypesIds(state);

    return appointmentApi.getAppointment(
      storeId,
      [mainAppointmentTypeId, ...relatedAppointmentTypesIds].join(':'),
      bypassErrorCodes,
    );
  };

export const loadAppointment = (storeId) => (dispatch, getState) =>
  getAppointment(storeId)(dispatch, getState).then((response) => {
    dispatch(setAppointment(response));
  });

export const updateAppointment =
  (updateRequest = {}, selectedShopId) =>
  (dispatch, getState) => {
    const state = getState();

    const currentStoreId = selectedShopId || appointmentDraftSelectors.selectShop(state).id;
    const mainAppointmentType = appointmentDraftSelectors.selectAppointmentType(state);
    const relatedAppointmentTypeLocalId =
      appointmentDraftSelectors.selectRelatedAppointmentTypeById()(state).localId;

    const { relatedAppointmentRequests, ...mainAppointmentUpdateRequest } =
      appointmentRequestHelpers.prepareUpdateAppointmentRequest(state, updateRequest);

    return appointmentApi.updateAppointment(mainAppointmentType.id, {
      currentStoreId,
      ...mainAppointmentUpdateRequest,
      localAppointmentTypeId: mainAppointmentType.localId,
      relatedAppointmentRequests: relatedAppointmentRequests.map((relatedAppointment) => ({
        ...relatedAppointment,
        localAppointmentTypeId: relatedAppointmentTypeLocalId,
      })),
    });
  };

export const clearAppointment = () => (dispatch) => dispatch(setAppointment(null));

const getAddress = (booking, appointmentTypeId) => {
  const { addressQuestionId } =
    envConfig.services[api.SERVICE_NAME].appointmentTypes[appointmentTypeId];
  const addressAnswer = booking.answers.find(({ questionId }) => questionId === addressQuestionId);
  return addressAnswer && restoreAnswers.restoreAddressAnswer(addressAnswer.answers);
};

export const getReduxDraftFromBooking = (booking, appointmentTypeId) => ({
  ...booking,
  shop: { ...booking.store, address: booking.store.address.addressLines },
  dateSlot: booking.slot.date,
  timeSlot: booking.slot.time,
  address: getAddress(booking, appointmentTypeId),
});

export const loadBookingDetails = (bookingId) => (dispatch, getState) => {
  const {
    partners: { partners },
  } = getState();

  return bookingsApi.getBooking(bookingId).then((booking) => {
    const {
      items: [bookingItem],
    } = booking;

    const {
      store: { id: shopId },
      answers: bookingAnswers,
      appointmentType: { id: appointmentTypeId },
      department: { id },
    } = bookingItem;

    const serviceName = Object.keys(envConfig.services).find(
      (service) =>
        id === envConfig.services[service].id &&
        Object.keys(envConfig.services[service].appointmentTypes).find(
          (appointmentType) => appointmentType === appointmentTypeId,
        ),
    );
    const config = getServiceConfig(serviceName, appointmentTypeId);

    dispatch(
      appointmentDraftActions.setDraftAppointment(
        getReduxDraftFromBooking(bookingItem, appointmentTypeId),
      ),
    );
    dispatch(
      setAppointment({
        storeId: shopId,
        answers: bookingAnswers,
      }),
    );

    const isLoadedPartners = partners.length > 0;
    dispatch(setConfig(config));
    return Promise.all([
      dispatch(loadClientDetails()),
      !isLoadedPartners && dispatch(loadPartners()),
      dispatch(questionsActions.retrieveQuestionsForShop(shopId, appointmentTypeId)),
    ]).then(() => {
      const {
        appointment: { draft },
        client: { details: clientDetails },
        questions: { questions },
      } = getState();

      const allQuestions = Object.values(questions).reduce((output, categoryQuestions) => {
        const concatenatedQuestions = output.concat(categoryQuestions);
        return concatenatedQuestions.reduce(
          (acc, curr) =>
            curr.children && curr.children.length
              ? [...acc, curr, ...curr.children]
              : [...acc, curr],
          [],
        );
      }, []);

      const answers = {};
      draft.answers.forEach((answer) => {
        const question = allQuestions.find((el) => el.id === answer.questionId);

        if (question) {
          answers[question.reduxFieldConfig.name] = question.restoreAnswer({
            question,
            answers: answer.answers,
          });
        }
      });

      dispatch(
        questionsActions.setRestoredAnswers({
          yourDetailsSection: {
            ...clientDetails,
          },
          questionsSection: {
            ...answers,
          },
        }),
      );
      return bookingItem;
    });
  });
};

export const createAppointment =
  (...appointmentTypeIds) =>
  (dispatch, getState) => {
    const createAppointmentRequest = buildCreateAppointmentRequest(getState());

    return appointmentApi.createAppointment(appointmentTypeIds.join(':'), createAppointmentRequest);
  };

export const deleteAppointment = (storeId) => (dispatch, getState) => {
  const {
    appointment: {
      draft: {
        appointmentType: { id: appointmentTypeId },
      },
    },
  } = getState();

  appointmentApi
    .deleteAppointment(storeId, appointmentTypeId)
    .then(dispatch(setAppointment(null)))
    .catch(console.error);
};

export const appointmentActions = {
  getAppointment,
  loadAppointment,
  clearAppointment,
  loadBookingDetails,
  deleteAppointment,
  updateAppointment,
  createAppointment,
};
